import { Effect, FiberId } from 'effect';
import type { RuntimeFiber } from 'effect/Fiber';
import { useEffect, useMemo, useRef, useState } from 'react';

type Debouncer = {
  run: (fn: () => void) => void;
  cancel: () => void;
};

const createDebouncer = (delayMs: number): Debouncer => {
  let fiber: RuntimeFiber<void, never> | null = null;

  const cancel = () => {
    fiber?.unsafeInterruptAsFork(FiberId.none);
    fiber = null;
  };

  const run = (fn: () => void) => {
    cancel();
    fiber = Effect.runFork(Effect.sleep(delayMs).pipe(Effect.zipRight(Effect.sync(fn))));
  };

  return { run, cancel };
};

export const useDebounce = <T>(value: T, delay = 300): T => {
  const [debounced, setDebounced] = useState<T>(value);
  const debouncerRef = useRef<Debouncer | null>(null);

  const debouncer = useMemo(() => {
    debouncerRef.current?.cancel();
    const next = createDebouncer(delay);
    debouncerRef.current = next;
    return next;
  }, [delay]);

  useEffect(() => {
    return () => {
      debouncer.cancel();
    };
  }, [debouncer]);

  useEffect(() => {
    debouncer.run(() => setDebounced(value));
  }, [value, debouncer]);

  return debounced;
};

export type DebouncedFunction<Args extends Array<unknown>> = ((...args: Args) => void) & {
  cancel: () => void;
};

export const useDebouncedCallback = <Args extends Array<unknown>>(
  callback: (...args: Args) => void,
  delay = 300,
): DebouncedFunction<Args> => {
  const debouncerRef = useRef<Debouncer | null>(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncer = useMemo(() => {
    debouncerRef.current?.cancel();
    const next = createDebouncer(delay);
    debouncerRef.current = next;
    return next;
  }, [delay]);

  useEffect(() => {
    return () => {
      debouncer.cancel();
    };
  }, [debouncer]);

  return useMemo(() => {
    const runner = ((...args: Args) =>
      debouncer.run(() => callbackRef.current(...args))) as DebouncedFunction<Args>;
    runner.cancel = () => debouncer.cancel();
    return runner;
  }, [debouncer]);
};
